import { useEffect, useState } from "react";

/**
 * 요소의 텍스트가 잘려서 말줄임(…) 처리되었는지 감지한다.
 * 1줄 말줄임은 가로로 넘치고, 2줄 이상 line-clamp는 세로로 넘치므로 두 방향을 모두 확인한다.
 *
 * @param {React.RefObject<HTMLElement>|HTMLElement|null} target 측정 대상 요소의 ref 또는 요소 자체.
 *      조건부로 렌더링되는 요소라면 ref 객체 대신 콜백 ref(useState)로 요소를 전달해야 마운트 시점에 재측정된다.
 * @param {Array} deps 값이 바뀌면 다시 측정할 의존성 (텍스트, line clamp 수 등).
 *      effect 의존성 배열에 펼쳐지므로 렌더마다 **길이가 일정해야 한다**.
 * @returns {boolean} 잘림 발생 여부
 */
const useTextOverflow = (target, deps = []) => {
    const [isOverflow, setIsOverflow] = useState(false);

    useEffect(() => {
        const el = target && typeof target === "object" && "current" in target ? target.current : target;
        if (!el) return undefined;

        let rafId = null;
        // 서브픽셀 반올림으로 인한 오탐을 막기 위해 1px 여유를 둔다.
        const check = () => setIsOverflow(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);

        check();

        const observer = new ResizeObserver(() => {
            // 한 프레임 안에서 여러 번 통지되어도 마지막 측정만 남기고, 언마운트 후 콜백이 남지 않게 한다.
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(check);
        });
        observer.observe(el);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, ...deps]);

    return isOverflow;
};

export default useTextOverflow;
